import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarPlus, Sparkles, Linkedin, Twitter, Instagram, Facebook, AtSign, Cloud } from "lucide-react";

const ALL_PLATFORMS = [
  { id: "linkedin", name: "LinkedIn", Icon: Linkedin },
  { id: "x", name: "X", Icon: Twitter },
  { id: "instagram", name: "Instagram", Icon: Instagram },
  { id: "facebook", name: "Facebook", Icon: Facebook },
  { id: "threads", name: "Threads", Icon: AtSign },
  { id: "bluesky", name: "Bluesky", Icon: Cloud },
];

function tomorrowYMD() {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default function ScheduleWeekDialog({ open, onOpenChange, projectId, defaultTopic = "", onCompleted }) {
  const [topic, setTopic] = useState(defaultTopic);
  const [platforms, setPlatforms] = useState(["linkedin", "x"]);
  const [tone, setTone] = useState("professional");
  const [weeks, setWeeks] = useState(2);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [startDate, setStartDate] = useState(tomorrowYMD());
  const [postTime, setPostTime] = useState("09:00");
  const [busy, setBusy] = useState(false);

  const toggle = (id) =>
    setPlatforms((arr) => (arr.includes(id) ? arr.filter((p) => p !== id) : [...arr, id]));

  const totalPosts = weeks * daysPerWeek * platforms.length;

  const submit = async () => {
    if (!topic.trim()) { toast.error("Pick a topic"); return; }
    if (platforms.length === 0) { toast.error("Pick at least one platform"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/social/schedule-week`, {
        topic, platforms, tone, weeks, days_per_week: daysPerWeek,
        start_date: startDate, post_time: postTime, include_hashtags: true, project_id: projectId,
      });
      toast.success(`${data.created} posts scheduled across ${data.summary?.days_scheduled || 0} days`);
      onCompleted?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-xl border border-foreground/20" data-testid="schedule-week-dialog">
        <DialogHeader>
          <DialogTitle className="font-semibold tracking-tight text-2xl flex items-center gap-2">
            <CalendarPlus className="w-5 h-5" /> Auto-schedule a week
          </DialogTitle>
          <DialogDescription className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">
            // AI writes + queues ~{totalPosts} posts in one click
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-mono-data text-[10px] uppercase tracking-widest">Topic / angle</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. 'launch announcements for our new feature'"
              className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
              data-testid="schedule-week-topic" />
          </div>

          <div>
            <Label className="font-mono-data text-[10px] uppercase tracking-widest">Platforms</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {ALL_PLATFORMS.map(({ id, name, Icon }) => (
                <button key={id} type="button" onClick={() => toggle(id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono-data uppercase tracking-widest ${
                    platforms.includes(id)
                      ? "bg-foreground text-background border-foreground"
                      : "bg-white border-border hover:border-foreground"
                  }`}
                  data-testid={`schedule-week-platform-${id}`}>
                  <Icon className="w-3.5 h-3.5" /> {name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Weeks</Label>
              <Select value={String(weeks)} onValueChange={(v) => setWeeks(Number(v))}>
                <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="schedule-week-weeks"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {[1,2,3,4].map((n) => <SelectItem key={n} value={String(n)}>{n} week{n>1?"s":""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Posting days / week</Label>
              <Select value={String(daysPerWeek)} onValueChange={(v) => setDaysPerWeek(Number(v))}>
                <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="schedule-week-days"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="1">1 · Mon</SelectItem>
                  <SelectItem value="2">2 · Mon, Thu</SelectItem>
                  <SelectItem value="3">3 · Mon, Wed, Fri</SelectItem>
                  <SelectItem value="4">4 · Mon-Fri minus Wed</SelectItem>
                  <SelectItem value="5">5 · weekdays</SelectItem>
                  <SelectItem value="6">6 · weekdays + Sat</SelectItem>
                  <SelectItem value="7">7 · daily</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="schedule-week-tone"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="playful">Playful</SelectItem>
                  <SelectItem value="contrarian">Contrarian</SelectItem>
                  <SelectItem value="authoritative">Authoritative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground font-mono-data text-sm"
                data-testid="schedule-week-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Time</Label>
              <Input type="time" value={postTime} onChange={(e) => setPostTime(e.target.value)}
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground font-mono-data text-sm"
                data-testid="schedule-week-time" />
            </div>
          </div>

          <div className="border border-foreground/15 bg-secondary/40 px-3 py-2 font-mono-data text-xs flex items-center justify-between" data-testid="schedule-week-summary">
            <span>{weeks * daysPerWeek} posting day{weeks * daysPerWeek === 1 ? "" : "s"} × {platforms.length} platform{platforms.length === 1 ? "" : "s"}</span>
            <span className="font-semibold">{totalPosts} posts queued</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none h-10" data-testid="schedule-week-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || platforms.length === 0 || !topic.trim()}
            className="rounded-none h-10 bg-foreground hover:bg-foreground/90" data-testid="schedule-week-submit">
            <Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "Writing & scheduling…" : `Schedule ${totalPosts} posts`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
